import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  Avatar,
  Paper,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import { styled, useTheme } from "@mui/system";
import {
  BarChart2,
  Users,
  CheckCircle,
  XCircle,
  Archive,
  UserX,
  ActivityIcon,
  VerifiedIcon,
  XIcon,
  FileText,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Home,
  Hammer,
  Wrench,
  Package,
  Wallet
} from "lucide-react";
import "@fontsource/plus-jakarta-sans";
import { Grafik } from "./Grafik";
import { GrafikDashboard } from "./GrafikDashboard";
import { GrafikVerifikasiDashboard } from "./GrafikVerifikasiDashboard";
import GrafikRegisterUsulan from "./GrafikRegisterUsulan";
import { getBantuanRekap } from "./API/bantuan";

// Styled components
const MainContent = styled(Box)(({ theme }) => ({
  background: "#f5f7fa",
  minHeight: "calc(100vh - 64px)",
  padding: "24px",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  transition: "transform 0.3s, box-shadow 0.3s",
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  },
}));

const CardIconWrapper = styled(Avatar)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor || "#4338CA",
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "20px",
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: "32px",
  fontWeight: 700,
  marginBottom: "8px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: "#64748B",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontWeight: 600,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 700,
  marginBottom: "24px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const WelcomeCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  marginBottom: "24px",
  padding: "24px",
  backgroundSize: "cover",
  backgroundPosition: "center",
  overflow: "hidden",
  position: "relative",
}));

const WelcomeContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
}));

const WelcomeOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "linear-gradient(45deg, rgba(67, 56, 202, 0.7) 0%, rgba(79, 70, 229, 0.4) 100%)",
  zIndex: 0,
}));

export const Dashboard2 = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:800px)');
  const isTablet = useMediaQuery('(min-width:801px) and (max-width:960px)');
  const { user } = useSelector((state) => state.authAdmin || {});
  const [data, setData] = useState(null);
  const [registerUsulanData, setRegisterUsulanData] = useState(null);
  const [bantuanRekap, setBantuanRekap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
  
        const [allResponse, verifikasiResponse, registerUsulanResponse, rekapResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_URL}/all`, { withCredentials: true }),
          axios.get(`${process.env.REACT_APP_URL}/dataverifikasi`, { withCredentials: true }),
          axios.get(`${process.env.REACT_APP_URL}/getgrafikregisterusulan`, { withCredentials: true }),
          getBantuanRekap().catch(() => null),
        ]);
  
        const allData = allResponse.data.data;
        const verifikasiOverall = verifikasiResponse.data.data.find(item => item.statType === "overall");
        const registerUsulan = registerUsulanResponse.data.data[0]; // Ambil data pertama dari array

        const combinedData = {
          ...allData,
          dataTerverifikasi: verifikasiOverall?.dataTerverifikasi || 0,
          BisaDapatBantuan: verifikasiOverall?.BisaDapatBantuan || 0,
          BelumBisaDapatBantuan: verifikasiOverall?.BelumBisaDapatBantuan || 0
        };
  
        setData(combinedData);
        setRegisterUsulanData(registerUsulan);
        setBantuanRekap(rekapResponse?.data?.data?.global || null);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAllData();
  }, []);

  // Function to get grid size for each card
  const getCardGridSize = (cardTitle, isMobile) => {
    if (isMobile) {
      return { xs: 12 };
    }
    
    // Desktop/tablet layout
    switch (cardTitle) {
      case "Backlog":
        return { xs: 12, sm: 6, md: 6, lg: 3 }; // Smaller width for backlog
      case "Data Terverifikasi":
      case "Belum Bisa Dapat Bantuan":
      case "Bisa Dapat Bantuan":
        return { xs: 12, sm: 6, md: 4, lg: 3 }; // Verification cards
      case "Total Usulan":
      case "Dalam Proses":
      case "Disetujui":
      case "Ditolak":
        return { xs: 12, sm: 6, md: 3, lg: 3 }; // Register usulan cards
      default:
        return { xs: 12, sm: 6, md: 4, lg: 3 }; // Default size
    }
  };

  const statsCards = [
    {
      title: "Layak Huni",
      value: data?.totalLayakHuni || 0,
      icon: <CheckCircle size={24} color="#fff" />,
      color: "#4F46E5",
    },
    {
      title: "Tidak Layak Huni",
      value: data?.totalTidakLayakHuni || 0,
      icon: <XCircle size={24} color="#fff" />,
      color: "#F43F5E",
    },
    {
      title: "Berpenghuni",
      value: data?.totalBerpenghuni || 0,
      icon: <Users size={24} color="#fff" />,
      color: "#10B981",
    },
    {
      title: "Tidak Berpenghuni",
      value: data?.totalTidakBerpenghuni || 0,
      icon: <UserX size={24} color="#fff" />,
      color: "#FB923C",
    },
    {
      title: "Total Data Di Input",
      value: (data?.totalBerpenghuni || 0) + (data?.totalTidakBerpenghuni || 0),
      icon: <ActivityIcon size={24} color="#fff" />,
      color: "#6366F1",
    },
    {
      title: "Backlog",
      value: data?.totalBacklog || 0,
      icon: <Archive size={24} color="#fff" />,
      color: "#6366F1",
    },
   
  ];
  const verifikasiCards = [
 {
      title: "Data Terverifikasi",
      value: data?.dataTerverifikasi || 0,
      icon: <VerifiedIcon size={24} color="#fff" />,
      color: "#10B981",
    },
    {
      title: "Belum Bisa Dapat Bantuan",
      value: data?.BelumBisaDapatBantuan || 0,
      icon: <XIcon size={24} color="#fff" />,
      color: "#F43F5E",
    },
    {
      title: "Bisa Dapat Bantuan",
      value: data?.BisaDapatBantuan || 0,
      icon: <CheckCircle size={24} color="#fff" />,
      color: "#10B981",
    }
  ]

  // Register Usulan Cards
  const registerUsulanCards = [
    {
      title: "Total Usulan",
      value: registerUsulanData?.totalData || 0,
      icon: <FileText size={24} color="#fff" />,
      color: "#3b82f6",
      description: "Total semua usulan"
    },
    {
      title: "Dalam Proses",
      value: registerUsulanData?.totalStatusProses || 0,
      icon: <Clock size={24} color="#fff" />,
      color: "#f59e0b",
      description: "Usulan sedang Diproses"
    },
    {
      title: "Disetujui",
      value: registerUsulanData?.totalStatusDisetujui || 0,
      icon: <ThumbsUp size={24} color="#fff" />,
      color: "#22c55e",
      description: "Usulan yang disetujui"
    },
    {
      title: "Ditolak",
      value: registerUsulanData?.totalStatusDitolak || 0,
      icon: <ThumbsDown size={24} color="#fff" />,
      color: "#ef4444",
      description: "Usulan yang ditolak"
    }
  ];

  if (loading) {
    return (
      <Box className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Container 
        maxWidth={false} 
        sx={{ 
          pt: 0,
          pb: 0,
          ml: { xs: 0, sm: isMobile ? 0 : "230px" }, 
          pr: { xs: 2, sm: 4 },
          pl: { xs: 2, sm: isMobile ? 2 : 0 },
          width: { xs: "100%", sm: isMobile ? "100%" : "calc(100% - 210px)" },
          mt: 0
        }}
      >
        {/* Main Content */}
        <Box sx={{ pt: 0, pb: 2 }}>
          {/* Welcome Card */}
          <WelcomeCard sx={{ mt: 0 }}>
            <WelcomeOverlay />
            <WelcomeContent>
              <Typography variant="h5" sx={{ 
                color: "white", 
                fontWeight: 700, 
                mb: 1, 
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: isMobile ? "1.1rem" : "1.5rem"
              }}>
                Selamat Datang, {user?.username || "Admin"}!
              </Typography>
              <Typography variant="body2" sx={{ 
                color: "white", 
                opacity: 0.9, 
                mb: 3, 
                maxWidth: "600px",
                fontSize: isMobile ? "0.8rem" : "0.875rem"
              }}>
                Sistem Informasi Digital Layanan dan Manajemen Perumahan (SI-DALIMAH) menyediakan informasi mengenai keadaan rumah di Kabupaten Sumbawa Barat.
              </Typography>
            </WelcomeContent>
          </WelcomeCard>
  
          {/* Stats Section - Rumah */}
          <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px" }}>
            <BarChart2 size={isMobile ? 20 : 24} />
            Statistik Rumah
          </SectionTitle>
  
          <Grid container spacing={isMobile ? 2 : 3}>
            {statsCards.map((card, index) => {
              const gridProps = getCardGridSize(card.title, isMobile);
              return (
                <Grid 
                  item 
                  {...gridProps}
                  key={index}
                >
                  <StyledCard>
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: "100%", 
                      display: "flex", 
                      flexDirection: "column" 
                    }}>
                      <CardIconWrapper 
                        bgcolor={card.color}
                        sx={{ 
                          width: isMobile ? 48 : 56, 
                          height: isMobile ? 48 : 56 
                        }}
                      >
                        {card.icon}
                      </CardIconWrapper>
                      <StatValue sx={{ fontSize: isMobile ? "24px" : "32px" }}>
                        {card.value}
                      </StatValue>
                      <StatLabel sx={{ fontSize: isMobile ? "12px" : "14px" }}>
                        {card.title}
                      </StatLabel>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
            <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px", mt: 4 }}>
            <FileText size={isMobile ? 20 : 24} />
            Statistik Verifikasi Bantuan Bangun Rumah Baru
          </SectionTitle>
 <Grid container spacing={isMobile ? 2 : 3}>
            {verifikasiCards.map((card, index) => {
              const gridProps = getCardGridSize(card.title, isMobile);
              return (
                <Grid 
                  item 
                  {...gridProps}
                  key={index}
                >
                  <StyledCard>
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: "100%", 
                      display: "flex", 
                      flexDirection: "column" 
                    }}>
                      <CardIconWrapper 
                        bgcolor={card.color}
                        sx={{ 
                          width: isMobile ? 48 : 56, 
                          height: isMobile ? 48 : 56 
                        }}
                      >
                        {card.icon}
                      </CardIconWrapper>
                      <StatValue sx={{ fontSize: isMobile ? "24px" : "32px" }}>
                        {card.value}
                      </StatValue>
                      <StatLabel sx={{ fontSize: isMobile ? "12px" : "14px" }}>
                        {card.title}
                      </StatLabel>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: "#94a3b8",
                          mt: 1,
                          fontSize: isMobile ? "10px" : "12px"
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
          {/* Register Usulan Section */}
          <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px", mt: 4 }}>
            <FileText size={isMobile ? 20 : 24} />
            Statistik Register Usulan Bantuan Rumah
          </SectionTitle>

          <Grid container spacing={isMobile ? 2 : 3}>
            {registerUsulanCards.map((card, index) => {
              const gridProps = getCardGridSize(card.title, isMobile);
              return (
                <Grid 
                  item 
                  {...gridProps}
                  key={index}
                >
                  <StyledCard>
                    <CardContent sx={{ 
                      p: isMobile ? 2 : 3, 
                      height: "100%", 
                      display: "flex", 
                      flexDirection: "column" 
                    }}>
                      <CardIconWrapper 
                        bgcolor={card.color}
                        sx={{ 
                          width: isMobile ? 48 : 56, 
                          height: isMobile ? 48 : 56 
                        }}
                      >
                        {card.icon}
                      </CardIconWrapper>
                      <StatValue sx={{ fontSize: isMobile ? "24px" : "32px" }}>
                        {card.value}
                      </StatValue>
                      <StatLabel sx={{ fontSize: isMobile ? "12px" : "14px" }}>
                        {card.title}
                      </StatLabel>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: "#94a3b8",
                          mt: 1,
                          fontSize: isMobile ? "10px" : "12px"
                        }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>

          {/* Summary Card for Register Usulan */}
          {registerUsulanData && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Card 
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white'
                }}
              >
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  gutterBottom
                  sx={{ fontSize: isMobile ? "14px" : "16px", display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FileText size={18} /> Ringkasan Register Usulan
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: isMobile ? "12px" : "14px"
                  }}
                >
                  Total {registerUsulanData.totalData || 0} usulan dengan {registerUsulanData.totalStatusProses || 0} dalam proses, 
                  {registerUsulanData.totalStatusDisetujui || 0} disetujui, dan {registerUsulanData.totalStatusDitolak || 0} ditolak.
                  {registerUsulanData.lastUpdated && ` Terakhir diperbarui: ${new Date(registerUsulanData.lastUpdated).toLocaleDateString('id-ID')}`}
                </Typography>
              </Card>
            </Grid>
          )}
          {/* BANTUAN */}
        <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px", mt: 4 }}>
            <FileText size={isMobile ? 20 : 24} />
            Statistik Bantuan Rumah
          </SectionTitle>

          <Grid container spacing={isMobile ? 2 : 3}>
            {[
              { title: "Total Bantuan", value: bantuanRekap?.totalBantuan || 0, icon: <Home size={24} color="#fff" />, color: "#4338CA", desc: "Total semua bantuan" },
              { title: "Warga Dapat Bantuan", value: bantuanRekap?.totalWargaMendapatBantuan || 0, icon: <Users size={24} color="#fff" />, color: "#10B981", desc: "Total warga/KK terbantu" },
              { title: "Total Nominal", value: `Rp ${(bantuanRekap?.totalNominalBantuan || 0).toLocaleString('id-ID')}`, icon: <Wallet size={24} color="#fff" />, color: "#0EA5E9", desc: "Akumulasi nominal bantuan" },
              { title: "Pembangunan", value: bantuanRekap?.totalPembangunan || 0, icon: <Hammer size={24} color="#fff" />, color: "#F59E0B", desc: "Bantuan pembangunan baru" },
        
              { title: "Rehabilitasi", value: bantuanRekap?.totalRehab || 0, icon: <Wrench size={24} color="#fff" />, color: "#8B5CF6", desc: "Rehabilitasi rumah" },
            
            ].map((card, index) => (
              <Grid item key={index} {...getCardGridSize(card.title, isMobile)}>
                <StyledCard>
                  <CardContent sx={{ p: isMobile ? 2 : 3, height: "100%", display: "flex", flexDirection: "column" }}>
                    <CardIconWrapper bgcolor={card.color} sx={{ width: isMobile ? 48 : 56, height: isMobile ? 48 : 56 }}>
                      {card.icon}
                    </CardIconWrapper>
                    <StatValue sx={{ fontSize: isMobile ? "24px" : "32px" }}>
                      {card.value}
                    </StatValue>
                    <StatLabel sx={{ fontSize: isMobile ? "12px" : "14px" }}>
                      {card.title}
                    </StatLabel>
                    <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, fontSize: isMobile ? "10px" : "12px" }}>
                      {card.desc}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

          {bantuanRekap && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, #0A5EB0 0%, #084B8A 100%)',
                  color: 'white'
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: isMobile ? "14px" : "16px", display: "flex", alignItems: "center", gap: 1 }}>
                  <FileText size={18} /> Ringkasan Bantuan Rumah
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: isMobile ? "12px" : "14px" }}>
                  Total {bantuanRekap.totalBantuan || 0} bantuan diberikan kepada {bantuanRekap.totalWargaMendapatBantuan || 0} warga,
                  terdiri dari {bantuanRekap.totalPembangunan || 0} pembangunan,
                  {bantuanRekap.totalRehab || 0} Rehabilitasi.
                  Total nominal: Rp {(bantuanRekap.totalNominalBantuan || 0).toLocaleString('id-ID')}.
                  {bantuanRekap.lastUpdated && ` Terakhir diperbarui: ${new Date(bantuanRekap.lastUpdated).toLocaleDateString('id-ID')}`}
                </Typography>
              </Card>
            </Grid>
          )}

           {/* BANTUAN */}
         <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px", mt: 4 }}>
            <FileText size={isMobile ? 20 : 24} />
            Keluhan Bantuan Rumah
          </SectionTitle>
          {/* Chart Section */}
          <Box sx={{ mt: isMobile ? 3 : 4 }}>
            <SectionTitle sx={{ fontSize: isMobile ? "18px" : "20px" }}>
              <BarChart2 size={isMobile ? 20 : 24} />
              Grafik Perbandingan
            </SectionTitle>
          </Box>
        </Box>
        
        {/* Existing Grafik Component */}
        <Grafik />
        
        {/* New Verification Chart Component */}
        <GrafikVerifikasiDashboard />

        <GrafikRegisterUsulan />
        
      </Container>
    </Box>
  );
};